import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { NetworkService } from '../../services/network.service';
import { Subscription, interval } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-connection-status',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  template: `
    <div class="connection-status" [ngClass]="statusClass">
      <div class="icon" [ngClass]="iconClass"></div>
      <span>{{ statusMessage | translate }}</span>
    </div>
  `,
  styles: [
    `
      .connection-status {
        position: fixed;
        bottom: 0;
        left: 0;
        right: 0;
        padding: 8px 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 9999;
        transition: transform 0.3s ease-in-out, opacity 0.3s ease-in-out;
        font-size: 14px;
        gap: 8px;
      }
      .online {
        background-color: #4caf50;
        color: white;
        transform: translateY(100%);
        opacity: 0;
      }
      .offline {
        background-color: #f44336;
        color: white;
        transform: translateY(0);
        opacity: 1;
      }
      .icon {
        width: 18px;
        height: 18px;
        display: inline-block;
        position: relative;
      }
      .icon-online {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M5 12.55a11 11 0 0 1 14.08 0'%3E%3C/path%3E%3Cpath d='M1.42 9a16 16 0 0 1 21.16 0'%3E%3C/path%3E%3Cpath d='M8.53 16.11a6 6 0 0 1 6.95 0'%3E%3C/path%3E%3Cline x1='12' y1='20' x2='12.01' y2='20'%3E%3C/line%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
      .icon-offline {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cline x1='1' y1='1' x2='23' y2='23'%3E%3C/line%3E%3Cpath d='M16.72 11.06A10.94 10.94 0 0 1 19 12.55'%3E%3C/path%3E%3Cpath d='M5 12.55a10.94 10.94 0 0 1 5.17-2.39'%3E%3C/path%3E%3Cpath d='M10.71 5.05A16 16 0 0 1 22.58 9'%3E%3C/path%3E%3Cpath d='M1.42 9a15.91 15.91 0 0 1 4.7-2.88'%3E%3C/path%3E%3Cpath d='M8.53 16.11a6 6 0 0 1 6.95 0'%3E%3C/path%3E%3Cline x1='12' y1='20' x2='12.01' y2='20'%3E%3C/line%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: center;
        background-size: contain;
      }
    `,
  ],
})
export class ConnectionStatusComponent implements OnInit, OnDestroy {
  // Connection status states
  private readonly STATUS_ONLINE = 'online';
  private readonly STATUS_OFFLINE = 'offline';

  // Current connection status
  connectionStatus = this.STATUS_ONLINE;
  
  // Flag to track initial load
  private isInitialLoad = true;

  // UI bindings
  get statusClass() {
    return {
      online: this.connectionStatus === this.STATUS_ONLINE,
      offline: this.connectionStatus === this.STATUS_OFFLINE,
    };
  }

  get iconClass() {
    return {
      'icon-online': this.connectionStatus === this.STATUS_ONLINE,
      'icon-offline': this.connectionStatus === this.STATUS_OFFLINE,
    };
  }

  get statusMessage() {
    switch (this.connectionStatus) {
      case this.STATUS_ONLINE:
        return 'CONNECTION.FAST';
      case this.STATUS_OFFLINE:
        return 'CONNECTION.OFFLINE';
      default:
        return 'CONNECTION.OFFLINE';
    }
  }

  private subscription = new Subscription();
  private pingInterval = 2000; // 2 seconds - more frequent checks
  private hideOnlineTimeout?: any;
  private autoHideDelay = 5000; // 5 seconds to auto-hide online status

  constructor(private networkService: NetworkService) {}

  ngOnInit() {
    // Check initial network status but don't display if online
    const initialOnlineStatus = this.networkService.isOnline();
    
    // Set initial status without visual notification if online
    this.connectionStatus = initialOnlineStatus ? this.STATUS_ONLINE : this.STATUS_OFFLINE;
    
    // Only show offline status on initial load
    if (!initialOnlineStatus) {
      // If offline on load, make sure it's visible
      setTimeout(() => {
        const offlineStatusElement = document.querySelector('.connection-status.offline');
        if (offlineStatusElement) {
          (offlineStatusElement as HTMLElement).style.transform = 'translateY(0)';
          (offlineStatusElement as HTMLElement).style.opacity = '1';
        }
      }, 0);
    }

    // After a short delay, mark initial load as complete
    setTimeout(() => {
      this.isInitialLoad = false;
    }, 1000);

    // Subscribe to network service status changes
    this.subscription.add(
      this.networkService.getOnlineStatus().subscribe((online) => {
        const previousStatus = this.connectionStatus;
        const newStatus = online ? this.STATUS_ONLINE : this.STATUS_OFFLINE;
        
        // Only process if status actually changed
        if (previousStatus !== newStatus) {
          this.setConnectionStatus(newStatus);
          
          // If coming back online, show and then auto-hide the status bar
          if (online) {
            this.showOnlineStatusBriefly();
          }
        }
      })
    );

    // Set up periodic active connectivity checks every few seconds
    this.subscription.add(
      interval(this.pingInterval).subscribe(() => {
        this.checkNetworkStatus();
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.hideOnlineTimeout) {
      clearTimeout(this.hideOnlineTimeout);
    }
  }

  /**
   * Performs a network connectivity check
   */
  private checkNetworkStatus() {
    const isOnline = this.networkService.isOnline();
    const currentStatus = this.connectionStatus;
    const newStatus = isOnline ? this.STATUS_ONLINE : this.STATUS_OFFLINE;
    
    // Only update if status changed (avoid flashing notifications)
    if (currentStatus !== newStatus) {
      this.setConnectionStatus(newStatus);
      
      // If transitioning to online state and not in initial load, show briefly
      if (isOnline && !this.isInitialLoad) {
        this.showOnlineStatusBriefly();
      }
    }
  }

  /**
   * Shows the online status indicator briefly before hiding it
   */
  private showOnlineStatusBriefly() {
    // Clear any existing timeout
    if (this.hideOnlineTimeout) {
      clearTimeout(this.hideOnlineTimeout);
    }
    
    // Show the online status
    setTimeout(() => {
      const onlineStatusElement = document.querySelector('.connection-status.online');
      if (onlineStatusElement) {
        (onlineStatusElement as HTMLElement).style.transform = 'translateY(0)';
        (onlineStatusElement as HTMLElement).style.opacity = '1';
      }
    }, 0);
    
    // Hide the online status after delay
    this.hideOnlineTimeout = setTimeout(() => {
      const onlineStatusElement = document.querySelector('.connection-status.online');
      if (onlineStatusElement && this.connectionStatus === this.STATUS_ONLINE) {
        (onlineStatusElement as HTMLElement).style.transform = 'translateY(100%)';
        (onlineStatusElement as HTMLElement).style.opacity = '0';
      }
    }, this.autoHideDelay);
  }

  /**
   * Updates the connection status and ensures proper UI state
   */
  private setConnectionStatus(status: string) {
    this.connectionStatus = status;
  }
}
